/**
 * synkro stop / start / restart — safe container lifecycle commands.
 *
 * These ensure PGLite data is always persisted to the host before shutdown,
 * and verify integrity on start. Agents should use these instead of raw
 * docker commands to avoid data corruption.
 */
import {
  dockerSafeStop, dockerSafeStart, dockerSafeRestart, assertDockerAvailable,
  resolveWorkerConfig, dockerUpdate, waitForContainerReady, waitForWorkersReady, readContainerConfig,
} from '../local-cc/dockerInstall.js';
import { detectGitRepo, reconcileHarness, syncSkillFiles, reconcileDeployLocation, recycleCloudContainer } from './install.js';

/**
 * The connected repo for a container recreate. start/restart/update all run
 * from inside the repo, so detect it live; fall back to whatever the existing
 * container recorded. Without this, recreating the container drops
 * SYNKRO_CONNECTED_REPO and the dashboard loses its project anchor.
 */
function resolveConnectedRepo(): string | undefined {
  return detectGitRepo() ?? readContainerConfig()?.connectedRepo ?? undefined;
}

export async function stopCommand(): Promise<void> {
  assertDockerAvailable();
  console.log('Synkro: stopping server\n');
  const result = await dockerSafeStop();
  if (!result.ok) {
    console.error('\nStop failed. Check: docker logs synkro-server');
    process.exit(1);
  }
  console.log('\nServer stopped.');
}

export async function startCommand(rest: string[] = []): Promise<void> {
  assertDockerAvailable();
  // With --workers / --provider(s) flags, recreate the container with the
  // requested pool config (container env is fixed at `docker run` time, so a
  // plain `docker start` can't change it). Without flags, plain safe-start.
  const cfg = resolveWorkerConfig(rest);
  if (cfg.explicit) {
    console.log(`Synkro: starting server (${cfg.claudeWorkers} claude + ${cfg.cursorWorkers} cursor)\n`);
    await dockerUpdate({ claudeWorkers: cfg.claudeWorkers, cursorWorkers: cfg.cursorWorkers, connectedRepo: resolveConnectedRepo() });
    const ready = await waitForContainerReady(60_000);
    if (!ready) { console.error('\n⚠ container did not pass /healthz within 60s'); process.exit(1); }
    console.log('\nServer is running.');
    return;
  }
  console.log('Synkro: starting server\n');
  const result = await dockerSafeStart();
  if (!result.ok) {
    console.error(`\nStart failed: ${result.error}`);
    process.exit(1);
  }
  console.log('\nServer is running.');
}

export async function updateCommand(): Promise<void> {
  assertDockerAvailable();
  // Read the installed container's pool config so the rebuilt container keeps
  // the same worker split instead of resetting to defaults.
  const cfg = readContainerConfig();
  if (!cfg) {
    console.error('No synkro-server container found. Run `synkro install` first.');
    process.exit(1);
  }
  const claudeWorkers = cfg.claudeWorkers ?? 8;
  const cursorWorkers = cfg.cursorWorkers ?? 0;
  console.log('Synkro: updating to the latest container image');
  console.log(`  preserving pool: ${claudeWorkers} claude + ${cursorWorkers} cursor worker(s)\n`);
  // dockerUpdate: graceful stop (snapshot + checkpoint) → remove → pull latest → recreate.
  await dockerUpdate({ claudeWorkers, cursorWorkers, connectedRepo: resolveConnectedRepo() });
  const ready = await waitForContainerReady(90_000);
  if (!ready) {
    console.error('\n⚠ container did not pass its health check within 90s — check: docker logs synkro-server');
    process.exit(1);
  }
  console.log('\nSynkro updated — now running the latest version.');
}

export async function restartCommand(rest: string[] = []): Promise<void> {
  // First reconcile the deploy location from synkro.toml. If the user flipped
  // [grader] location, this switches local<->cloud: local→cloud safely stops +
  // removes the local container and provisions the CF container; cloud→local
  // repoints config so the docker (re)start below brings the local grader back.
  const { location, changed } = await reconcileDeployLocation();
  if (location === 'cloud') {
    // Cloud grader: a restart spins a NET-NEW container. If we just switched
    // local→cloud, reconcile already provisioned + verified a fresh one — don't
    // double-boot. Otherwise (already cloud), destroy the running container and
    // boot a fresh one so the user gets a clean, single, net-new instance.
    if (!changed) await recycleCloudContainer();
    return;
  }

  assertDockerAvailable();
  const cfg = resolveWorkerConfig(rest);

  // Reconcile harness from .synkro (hooks, MCP, worker allocation)
  let claudeWorkers = cfg.claudeWorkers;
  let cursorWorkers = cfg.cursorWorkers;
  if (!cfg.explicit) {
    const reconciled = reconcileHarness();
    if (reconciled) {
      claudeWorkers = reconciled.claudeWorkers;
      cursorWorkers = reconciled.cursorWorkers;
    }
  }

  console.log(`Synkro: restarting server (${claudeWorkers} claude + ${cursorWorkers} cursor)\n`);
  await dockerUpdate({ claudeWorkers, cursorWorkers, connectedRepo: resolveConnectedRepo() });
  const ready = await waitForContainerReady(60_000);
  if (!ready) { console.error('\n⚠ container did not pass /healthz within 60s'); process.exit(1); }
  console.log('\nServer restarted successfully.');

  const workersUp = await waitForWorkersReady(30_000);
  if (workersUp) {
    console.log('✓ workers ready');
    await syncSkillFiles();
  } else {
    console.warn('⚠ workers did not register within 30s — skill sync skipped');
  }
}
